import { createAuthClient } from 'better-auth/react';
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

function getAuthBaseURL(): string {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  if (typeof window !== 'undefined' && window.location.hostname.endsWith('tenpify.com')) {
    return 'https://api.finca.tenpify.com';
  }
  return 'http://localhost:3001';
}

const baseURL = getAuthBaseURL();

// MODULE-PRIVATE: never re-exported (TS2742 mitigation).
// authClient is typed inline to avoid pnpm portability issue with composite:true
// + deep @better-auth/core internal types. We only use what we destructure below.
const _rawClient = createAuthClient({ baseURL }) as unknown;

interface AuthClientShape {
  signIn: { email: (opts: { email: string; password: string }) => Promise<{ data: unknown; error: { code?: string; message?: string } | null }> };
  signUp: { email: (opts: { name: string; email: string; password: string }) => Promise<{ data: unknown; error: { code?: string; message?: string } | null }> };
  signOut: () => Promise<unknown>;
  useSession: () => unknown;
  getSession: () => Promise<{ data: { user: { id: string; email: string; name: string; image?: string | null }; session: { id: string } } | null }>;
}

const authClient = _rawClient as AuthClientShape;

// Re-export only the destructured methods — their types are simple and resolvable.
export const { signIn, signUp, signOut, useSession } = authClient;

export type SessionUser = { id: string; email: string; name: string; image?: string | null };

export const sessionQueryOptions = queryOptions({
  queryKey: ['auth', 'session'] as const,
  queryFn: async () => {
    try {
      const res = await authClient.getSession();
      return res?.data ?? null;
    } catch {
      return null;
    }
  },
  staleTime: 4 * 60 * 1000,
  retry: false,
});

export function useSignOutMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await signOut();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['auth', 'session'] });
    },
  });
}
